<p-toast></p-toast>

@if (loading()) {
  <div class="loading-container">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading transcription...</p>
  </div>
} @else if (transcription()) {
  <div class="transcription-detail-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">{{ getTranscriptionTitle() }}</h1>
      <div class="action-buttons">
        @if (showResumePausedRecordingAction()) {
          <button class="action-btn action-btn--recording-outline" type="button" (click)="goResumePausedRecording()">
            <i class="pi pi-play"></i>
            Resume recording
          </button>
        }
        @if (showAddMoreRecordingAction()) {
          <button class="action-btn action-btn--recording-outline" type="button" (click)="goAddMoreRecording()">
            <i class="pi pi-microphone"></i>
            Add recording
          </button>
        }
        <button class="action-btn" (click)="onPrint()">
          <i class="pi pi-print"></i>
          Print
        </button>
      </div>
    </div>

    <!-- Patient Information Section -->
    <div class="patient-info-card">
      <h2 class="card-title">Patient Information</h2>
      
      <div class="row">
        <div class="col-6">
          <div class="info-row">
            <span class="info-label">Patient Name</span>
            <span class="info-value">{{ transcription()?.patientName || 'Unknown' }}</span>
          </div>
        </div>
        <div class="col-6">
          <div class="info-row">
            <span class="info-label">Date</span>
            <span class="info-value">{{ formatDate(transcription()?.date) }}</span>
          </div>
        </div>
      </div>
      
      <div class="row">
        <div class="col-6">
          <div class="info-row">
            <span class="info-label">Doctor</span>
            <span class="info-value">{{ transcription()?.createdByUserName || 'N/A' }}</span>
          </div>
        </div>
        <div class="col-6">
          <div class="info-row">
            <span class="info-label">MRN</span>
            <span class="info-value">{{ transcription()?.mrn || transcription()?.patientId || 'N/A' }}</span>
          </div>
        </div>
      </div>
      
      <div class="row">
        <div class="col-6">
          <div class="info-row">
            <span class="info-label">Duration</span>
            <span class="info-value">{{ transcription()?.duration }}</span>
          </div>
        </div>
        <div class="col-6">
          <div class="info-row">
            <span class="info-label">Status</span>
            <span class="info-value">{{ transcription()?.status || 'Completed' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Content Tabs -->
    <div class="content-tabs">
      <div class="custom-tabs">
        <div class="tab-list">
          <button 
            class="tab-button" 
            [class.active]="activeTabValue === 'soap-notes'"
            (click)="onTabChange('soap-notes')">
            SOAP Notes
          </button>
          <button 
            class="tab-button" 
            [class.active]="activeTabValue === 'transcription'"
            (click)="onTabChange('transcription')">
            Transcription
          </button>
          <button 
            class="tab-button" 
            [class.active]="activeTabValue === 'medical-terms'"
            (click)="onTabChange('medical-terms')">
            Medical Terms
          </button>
        </div>
        
        <div class="tab-content">
          <div *ngIf="activeTabValue === 'soap-notes'" class="tab-panel">
            <!-- SOAP Notes Section -->
            <div class="soap-notes-section">
              <div class="section-header">
                <div class="header-content">
                  <h2 class="section-title">SOAP Notes</h2>
                  <p class="section-subtitle">Structured clinical documentation</p>
                </div>
              </div>

              <div class="soap-content">
                <div class="soap-content-toolbar">
                  <button
                    pButton
                    type="button"
                    label="Copy All"
                    icon="pi pi-copy"
                    class="copy-all-btn"
                    (click)="copyAllSoapNotes()">
                  </button>
                </div>
                <!-- Visit Summary (AI Analysis Key Findings) -->
                <div class="soap-section visit-summary-section">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">Visit Summary</h3>
                    <div class="section-actions">
                      <button type="button" class="edit-toggle-btn" (click)="setEditingSoapSection(editingSoapSection() === 'visitSummary' ? null : 'visitSummary')">
                        {{ editingSoapSection() === 'visitSummary' ? 'Done' : 'Edit' }}
                      </button>
                      <button pButton type="button" icon="pi pi-copy" class="copy-btn" [cdkCopyToClipboard]="visitSummaryText()" (cdkCopyToClipboardCopied)="onCopied()"></button>
                    </div>
                  </div>
                  <div class="editable-content">
                    <div class="soap-formatted" *ngIf="editingSoapSection() !== 'visitSummary'" [innerHTML]="visitSummaryText() | soapToHtml:'visitSummary'"></div>
                    <textarea *ngIf="editingSoapSection() === 'visitSummary'" #soapTextarea class="soap-textarea soap-section-textarea visit-summary-textarea" rows="3" [value]="visitSummaryText()" (input)="onVisitSummaryInput($event)"></textarea>
                  </div>
                </div>

                <!-- Subjective -->
                <div class="soap-section">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">Subjective</h3>
                    <div class="section-actions">
                      <button type="button" class="edit-toggle-btn" (click)="setEditingSoapSection(editingSoapSection() === 'subjective' ? null : 'subjective')">{{ editingSoapSection() === 'subjective' ? 'Done' : 'Edit' }}</button>
                      <button pButton type="button" icon="pi pi-copy" class="copy-btn" [cdkCopyToClipboard]="soapNotes().subjective | bulletizeNumberedHeadings | soapNormalize:'subjective'" (cdkCopyToClipboardCopied)="onCopied()"></button>
                    </div>
                  </div>
                  <div class="editable-content">
                    <div class="soap-formatted" *ngIf="editingSoapSection() !== 'subjective'" [innerHTML]="soapNotes().subjective | bulletizeNumberedHeadings | soapNormalize:'subjective' | soapToHtml:'hpi'"></div>
                    <textarea *ngIf="editingSoapSection() === 'subjective'" #soapTextarea class="soap-textarea soap-section-textarea symptoms-textarea" rows="4" [value]="soapNotes().subjective ?? ''" (input)="onSoapNotesInput('subjective', $event)"></textarea>
                  </div>
                </div>

                <!-- Objective -->
                <div class="soap-section">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">Objective</h3>
                    <div class="section-actions">
                      <button type="button" class="edit-toggle-btn" (click)="setEditingSoapSection(editingSoapSection() === 'objective' ? null : 'objective')">{{ editingSoapSection() === 'objective' ? 'Done' : 'Edit' }}</button>
                      <button pButton type="button" icon="pi pi-copy" class="copy-btn" [cdkCopyToClipboard]="soapNotes().objective | bulletizeNumberedHeadings | soapNormalize:'objective'" (cdkCopyToClipboardCopied)="onCopied()"></button>
                    </div>
                  </div>
                  <div class="editable-content">
                    <div class="soap-formatted" *ngIf="editingSoapSection() !== 'objective'" [innerHTML]="soapNotes().objective | bulletizeNumberedHeadings | soapNormalize:'objective' | soapToHtml"></div>
                    <textarea *ngIf="editingSoapSection() === 'objective'" #soapTextarea class="soap-textarea soap-section-textarea objective-textarea" rows="8" [value]="soapNotes().objective ?? ''" (input)="onSoapNotesInput('objective', $event)"></textarea>
                    <p class="helper-text" *ngIf="editingSoapSection() === 'objective'">Use **bold** for section headers (Vitals, General, etc.)</p>
                  </div>
                </div>

                <!-- Assessment -->
                <div class="soap-section">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">Assessment</h3>
                    <div class="section-actions">
                      <button type="button" class="edit-toggle-btn" (click)="setEditingSoapSection(editingSoapSection() === 'assessment' ? null : 'assessment')">{{ editingSoapSection() === 'assessment' ? 'Done' : 'Edit' }}</button>
                      <button pButton type="button" icon="pi pi-copy" class="copy-btn" [cdkCopyToClipboard]="soapNotes().assessment | bulletizeNumberedHeadings | soapNormalize:'assessment' | soapSentenceBreaks" (cdkCopyToClipboardCopied)="onCopied()"></button>
                    </div>
                  </div>
                  <div class="editable-content">
                    <div class="soap-formatted" *ngIf="editingSoapSection() !== 'assessment'" [innerHTML]="soapNotes().assessment | bulletizeNumberedHeadings | soapNormalize:'assessment' | soapSentenceBreaks | soapToHtml"></div>
                    <textarea *ngIf="editingSoapSection() === 'assessment'" #soapTextarea class="soap-textarea soap-section-textarea assessment-textarea" rows="12" [value]="soapNotes().assessment ?? ''" (input)="onSoapNotesInput('assessment', $event)"></textarea>
                  </div>
                </div>

                <!-- Plan (same format as Assessment: Problem headings + bullet subsections with bold labels) -->
                <div class="soap-section">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">Plan</h3>
                    <div class="section-actions">
                      <button type="button" class="edit-toggle-btn" (click)="setEditingSoapSection(editingSoapSection() === 'plan' ? null : 'plan')">{{ editingSoapSection() === 'plan' ? 'Done' : 'Edit' }}</button>
                      <button pButton type="button" icon="pi pi-copy" class="copy-btn" [cdkCopyToClipboard]="soapNotes().plan | bulletizeNumberedHeadings | soapNormalize:'plan' | soapSentenceBreaks" (cdkCopyToClipboardCopied)="onCopied()"></button>
                    </div>
                  </div>
                  <div class="editable-content">
                    <div class="soap-formatted" *ngIf="editingSoapSection() !== 'plan'" [innerHTML]="soapNotes().plan | bulletizeNumberedHeadings | soapNormalize:'plan' | soapSentenceBreaks | soapToHtml"></div>
                    <textarea *ngIf="editingSoapSection() === 'plan'" #soapTextarea class="soap-textarea soap-section-textarea plan-textarea" rows="12" [value]="soapNotes().plan ?? ''" (input)="onSoapNotesInput('plan', $event)"></textarea>
                  </div>
                </div>

                <!-- Patient Instructions -->
                <div class="soap-section patient-instructions-section" *ngIf="transcription()">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">Patient Instructions</h3>
                    <div class="section-actions">
                      <button type="button" class="edit-toggle-btn" (click)="setEditingSoapSection(editingSoapSection() === 'patientInstructions' ? null : 'patientInstructions')">{{ editingSoapSection() === 'patientInstructions' ? 'Done' : 'Edit' }}</button>
                      <button pButton type="button" icon="pi pi-copy" class="copy-btn" [cdkCopyToClipboard]="patientInstructionsText() | soapNormalize:'patientInstructions'" (cdkCopyToClipboardCopied)="onCopied()"></button>
                    </div>
                  </div>
                  <div class="editable-content">
                    <div class="soap-formatted soap-formatted--patient-doc" *ngIf="editingSoapSection() !== 'patientInstructions'" [innerHTML]="patientInstructionsText() | soapNormalize:'patientInstructions' | soapToHtml:'patientInstructions'"></div>
                    <textarea *ngIf="editingSoapSection() === 'patientInstructions'" #soapTextarea class="soap-textarea soap-section-textarea patient-instructions-textarea" rows="6" [value]="patientInstructionsText() | soapNormalize:'patientInstructions'" (input)="onPatientInstructionsInput($event)"></textarea>
                  </div>
                </div>

                <!-- Treatment Plan Summary (AI Analysis) -->
                <div class="soap-section treatment-plan-summary-section" *ngIf="transcription()">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">Treatment Plan Summary</h3>
                    <div class="section-actions">
                      <button type="button" class="edit-toggle-btn" (click)="setEditingSoapSection(editingSoapSection() === 'treatmentPlanSummary' ? null : 'treatmentPlanSummary')">{{ editingSoapSection() === 'treatmentPlanSummary' ? 'Done' : 'Edit' }}</button>
                      <button
                        pButton
                        type="button"
                        icon="pi pi-copy"
                        class="copy-btn"
                        [cdkCopyToClipboard]="treatmentPlanSummaryText()"
                        (cdkCopyToClipboardCopied)="onCopied()">
                      </button>
                    </div>
                  </div>
                  <div class="editable-content">
                    <div class="soap-formatted soap-formatted--patient-doc" *ngIf="editingSoapSection() !== 'treatmentPlanSummary'" [innerHTML]="treatmentPlanSummaryText() | soapToHtml:'treatmentPlanSummary'"></div>
                    <textarea
                      *ngIf="editingSoapSection() === 'treatmentPlanSummary'"
                      #soapTextarea
                      class="soap-textarea soap-section-textarea treatment-plan-summary-textarea"
                      rows="6"
                      [value]="treatmentPlanSummaryText()"
                      (input)="onTreatmentPlanSummaryInput($event)">
                    </textarea>
                  </div>
                </div>

                <!-- ICD Codes (AI Analysis) -->
                <div class="soap-section icd-codes-section" *ngIf="icdCodesText()">
                  <div class="section-header-row">
                    <h3 class="soap-subtitle">ICD-10 Codes</h3>
                    <button
                      pButton
                      type="button"
                      icon="pi pi-copy"
                      class="copy-btn"
                      [cdkCopyToClipboard]="icdCodesText()"
                      (cdkCopyToClipboardCopied)="onCopied()">
                    </button>
                  </div>
                  <div class="editable-content">
                    <textarea
                      #soapTextarea
                      class="soap-textarea soap-section-textarea icd-codes-textarea"
                      rows="3"
                      readonly
                      [value]="icdCodesText()">
                    </textarea>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="soap-actions">
                  <div class="left-actions">
                    <button class="reset-btn" (click)="onResetSoapNotes()">Reset</button>
                    <button class="reanalyze-btn" (click)="onReanalyze()" [disabled]="isReanalyzing()">
                      <i class="pi pi-refresh" [class.pi-spin]="isReanalyzing()"></i>
                      {{ isReanalyzing() ? 'Re-analyzing...' : 'Re-analyze' }}
                    </button>
                  </div>
                  <button 
                    class="save-btn" 
                    [disabled]="!hasChanges"
                    (click)="onSaveSoapNotes()">
                    Save SOAP Notes
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div *ngIf="activeTabValue === 'transcription'" class="tab-panel">
            <!-- Transcription Section -->
            <div class="transcription-section">
              <div class="section-header">
                <div class="header-content">
                  <div class="title-with-icon">
                    <span class="file-icon">📄</span>
                    <h2 class="section-title">Transcription Content</h2>
                  </div>
                  <p class="section-subtitle">Full transcription of the consultation</p>
                </div>
              </div>

              @if (hasTranslatedTranscript()) {
                <div class="transcription-subtabs">
                  <div class="transcription-subtabs-left">
                    <button
                      class="subtab-button"
                      [class.active]="activeTranscriptTab() === 'translated'"
                      (click)="onTranscriptTabChange('translated')">
                      Translated (English)
                    </button>
                    <button
                      class="subtab-button"
                      [class.active]="activeTranscriptTab() === 'original'"
                      (click)="onTranscriptTabChange('original')">
                      Original
                    </button>
                  </div>
                  <button
                    type="button"
                    class="copy-btn"
                    (click)="copyTranscriptText(activeTranscriptTab())">
                    <i class="pi pi-copy"></i>
                  </button>
                </div>
              } @else {
                <div class="transcription-subtabs transcription-subtabs-single">
                  <button
                    type="button"
                    class="copy-btn"
                    (click)="copyTranscriptText()">
                    <i class="pi pi-copy"></i>
                  </button>
                </div>
              }
              
              <div class="transcription-content">
                <div class="transcription-text">
                  @if (loadingTranscript()) {
                    <div class="loading-spinner">
                      <p-progressSpinner></p-progressSpinner>
                      <p>Loading transcript...</p>
                    </div>
                  } @else if (hasTranslatedTranscript() && activeTranscriptTab() === 'translated' && translatedTranscriptText(); as transcript) {
                    <div class="full-transcript">
                      <div class="transcript-text-formatted" [innerHTML]="formatTranscript(transcript)"></div>
                    </div>
                  } @else if (hasTranslatedTranscript() && activeTranscriptTab() === 'original' && originalTranscriptText(); as transcript) {
                    <div class="full-transcript">
                      <div class="transcript-text-formatted" [innerHTML]="formatTranscript(transcript)"></div>
                    </div>
                  } @else if (fullTranscriptText(); as transcript) {
                    <div class="full-transcript">
                      <div class="transcript-text-formatted" [innerHTML]="formatTranscript(transcript)"></div>
                    </div>
                  } @else {
                    <p class="no-content">No transcript content available.</p>
                  }
                </div>
              </div>
            </div>
          </div>
          
          <div *ngIf="activeTabValue === 'medical-terms'" class="tab-panel">
            <!-- Medical Terms Section -->
            <div class="medical-terms-section">
              <div class="section-header">
                <div class="header-content">
                  <h2 class="section-title">Medical Terminology</h2>
                  <p class="section-subtitle">Definitions of specialized medical terms used in this transcription</p>
                </div>
              </div>
              
              <div class="medical-terms-content">
                @if (loadingAIAnalysis()) {
                  <div class="loading-spinner">
                    <p-progressSpinner></p-progressSpinner>
                    <p>Loading medical terms...</p>
                  </div>
                } @else if (aiAnalysisContent(); as content) {
                  <div class="terms-list">
                    @for (term of content.medicalTerms; track term.term) {
                      <div class="term-item">
                        <h3 class="term-title">{{ term.term }}</h3>
                        <p class="term-definition">{{ term.definition }}</p>
                      </div>
                    }
                  </div>
                } @else {
                  <p>No medical terms available</p>
                }
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>


  </div>
} @else {
  <div class="error-container">
    <h2>Transcription Not Found</h2>
    <p>The requested transcription could not be found.</p>
    <p-button 
      label="Back to Transcriptions"
      severity="secondary"
      (onClick)="onBack()">
    </p-button>
  </div>
}

